import React, { useState, useEffect } from 'react';
import { Box, Typography, Fade, Button, CircularProgress } from '@mui/material';
import { Tooltip } from "@mui/material";
import UserExperienceFeed from './UserExperienceFeed';
import api from '../utils/apiInterceptor';
import { API_URL } from '../constants/data';

const FeedbackComponent = (props) => {
  // Array to guarantee order: Excellent → Very Bad (left to right)
  const sentimentFiles = [
    { rating: 5, emoji: "😎", label: "Excellent" },
    { rating: 4, emoji: "😀", label: "Very Good" },
    { rating: 3, emoji: "🙂", label: "Good" },
    { rating: 2, emoji: "😐", label: "Poor" },
    { rating: 1, emoji: "😡", label: "Very Poor" },
  ];

  const [selectedRating, setSelectedRating] = useState(5); // Default to Excellent
  const [feedbackList, setFeedbackList] = useState([]);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [fetchedRatings, setFetchedRatings] = useState(new Set()); // Track which ratings have been fetched

  // Get rating counts from feedbackStats prop
  const ratingCounts = React.useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    if (props.feedbackStats?.feedback_stats) {
      props.feedbackStats.feedback_stats.forEach(item => {
        if (item.rating >= 1 && item.rating <= 5) {
          counts[item.rating] = item.count || 0;
        }
      });
    }

    return counts;
  }, [props.feedbackStats]);

  const totalFeedbackCount = React.useMemo(() => {
    return Object.values(ratingCounts).reduce((sum, count) => sum + count, 0);
  }, [ratingCounts]);
  const fetchFeedbackByRating = async (rating) => {
    try {
      setIsLoadingFeedback(true);

      const start_date = props.filters?.startDate
        ? new Date(props.filters.startDate).toISOString().split('T')[0]
        : new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];

      const end_date = props.filters?.endDate
        ? new Date(props.filters.endDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

      const response = await api.get(`${API_URL}/get_feedback_by_rating_v1/${rating}`, {
        params: {
          sector: props.filters?.Sector || 'All',
          companyname: props.filters?.CompanyName || 'All',
          category: props.filters?.Category || 'All',
          start_date,
          end_date,
          skip: 0,
          size: 100
        }
      });

      if (response.data?.feedback && Array.isArray(response.data.feedback)) {
        setFeedbackList(response.data.feedback);
        setFetchedRatings(prev => new Set([...prev, rating])); // Mark this rating as fetched
      } else {
        setFeedbackList([]);
      }
    } catch (error) {
      console.error(`Error fetching feedback for rating ${rating}:`, error);
      setFeedbackList([]);
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  useEffect(() => {
    if (selectedRating && !fetchedRatings.has(selectedRating)) {
      fetchFeedbackByRating(selectedRating);
    }
  }, [selectedRating]);

  useEffect(() => {
    if (props.feedbackStats && Object.keys(props.feedbackStats).length > 0) {
      setFetchedRatings(new Set());
      setFeedbackList([]);

      let newRating = 5;
      const ratingsToCheck = [5, 4, 3, 2, 1];
      for (const rating of ratingsToCheck) {
        if (ratingCounts[rating] > 0) {
          newRating = rating;
          break;
        }
      }

      if (newRating !== selectedRating) {
        setSelectedRating(newRating);
      } else {
        fetchFeedbackByRating(newRating);
      }
    }
  }, [props.feedbackStats, ratingCounts]);

  const handleRatingClick = (rating) => {
    setSelectedRating(rating);
    if (!fetchedRatings.has(rating)) {
      fetchFeedbackByRating(rating);
    } else {
      fetchFeedbackByRating(rating);
    }
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 }, height: "100%", display: "flex", flexDirection: "column", position: 'relative' }}>
        {props.isLoading && (
          <Box sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.6)',
            zIndex: 10,
          }}>
            <CircularProgress size={36} thickness={4} />
          </Box>
        )}

        {/* Feedback coverage progress bar */}
        {(() => {
          const total = props.totalCount || 0;
          const categorised = totalFeedbackCount || 0;
          const pct = total > 0 ? Math.min(100, (categorised / total) * 100) : 0;

          return (
            <Box sx={{ mb: 2, px: 0.5 }}>
              {/* Labels row */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f5dad6', border: '1.5px solid #e8b4ae', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.8rem' }}>
                    AI-Generated Ratings
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#c0735a', fontSize: '0.9rem' }}>
                    {categorised.toLocaleString()} <span style={{ color: '#222' }}>({pct.toFixed(1)}%)</span>
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#4a7fb5', fontSize: '0.9rem' }}>
                    {total.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
                    Total Feedback Records
                  </Typography>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#c6e1ff', border: '1.5px solid #9ec5f0', flexShrink: 0 }} />
                </Box>
              </Box>

              <Box
                sx={{
                  width: '100%',
                  height: 30,
                  borderRadius: 1,
                  border: '2px solid #c6e1ff',
                  bgcolor: '#c6e1ff',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 1px 3px #9ec5f0',
                }}
              >
                <Box
                  sx={{
                    width: `${pct}%`,
                    height: '100%',
                    // borderRadius: 0,
                    bgcolor: '#f5dad6',
                    transition: 'width 0.6s ease',
                    // boxShadow: '1px 0 6px #e8b4ae',
                  }}
                />
              </Box>

              {/* Percentage label
              <Typography
                variant="caption"
                sx={{ display: 'block', textAlign: 'right', mt: 0.5, color: 'text.disabled', fontSize: '0.9rem' }}
              >
                {pct.toFixed(1)}% Feedback Rated by AI
              </Typography> */}
            </Box>
          );
        })()}



        {/* Rating Selector - Ordered from Excellent to Very Bad */}
        <Box>
          <Box
            sx={{
              mt: { xs: 0.5, sm: 1, lg: 0 },
              mb: 1,
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              position: 'relative',
              zIndex: 2,
            }}
          >
            {sentimentFiles.map(({ rating, emoji, label }) => {
              const isSelected = selectedRating === rating;
              const count = ratingCounts[rating] || 0;

              return (
                <Tooltip key={rating} title={label} arrow placement="top">
                  <Button
                    onClick={() => handleRatingClick(rating)}
                    variant="text"
                    disabled={false}
                    sx={{
                      width: { xs: 100, sm: 90, md: 75, lg: 90, xl: 140 },
                      height: 'auto',
                      py: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 0.5,
                      borderRadius: 2,
                      backgroundColor: isSelected ? "action.hover" : "#D0CEFB",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        backgroundColor: "action.hover",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.2rem', md: '1.2rem', lg: '2rem', xl: '2rem' },

                          lineHeight: "1",
                          filter: isSelected ? "none" : "grayscale(0.3)",
                        }}
                      >
                        {emoji}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem', lg: '1rem', xl: '1rem' },
                          fontWeight: isSelected ? "bold" : "normal",

                          color: "text.secondary",
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.75rem',
                        color: isSelected ? "primary.main" : "text.secondary",
                        fontWeight: isSelected ? 600 : 400,

                        textTransform: 'none'
                      }}
                    >
                      {label}
                    </Typography>
                  </Button>
                </Tooltip>
              );
            })}
          </Box>

          {/* Feedback Feed */}
          <Box
            sx={{
              position: "relative",
              flex: 1,
              minHeight: { xs: 360, sm: 420 },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLoadingFeedback ? (
              <Box
                sx={{
                  p: 5,
                  textAlign: 'center',
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary">
                  Loading {sentimentFiles.find(s => s.rating === selectedRating)?.label.toLowerCase()} feedback...
                </Typography>
              </Box>
            ) : feedbackList.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'background.paper', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No {sentimentFiles.find(s => s.rating === selectedRating)?.label.toLowerCase()} feedback found
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <UserExperienceFeed
                  feedbackList={feedbackList}
                  selectedRating={selectedRating}
                  filters={props.filters}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default FeedbackComponent;